import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { ClientDto, ClientType, EntityType, IClientDto, ProjectDto } from '../../api/ClientApi';
import { RootState } from '../reducers';
import { myAssert } from '../../common/console';
import { WarehouseClient } from '../../api/Extensions';

export const createNewEmptyCustomer = createAction('client/createNewEmptyCustomer');
export const createNewEmptySupplier = createAction('client/createNewEmptySupplier');
export const clearClient = createAction('client/clearClient');

export const updateClientName = createAction<string>('client/updateClientName');
export const updateClientShortName = createAction<string>('client/updateClientShortName');
export const updateClientAddress = createAction<string>('client/updateClientAddress');
export const updateClientCity = createAction<string>('client/updateClientCity');
export const updateClientState = createAction<string>('client/updateClientState');
export const updateClientClientType = createAction<ClientType>('client/updateClientClientType');
export const updateClientContactInfo = createAction<string>('client/updateClientContactInfo');
export const updateClientEmail = createAction<string>('client/updateClientEmail');
export const updateClientEntityType = createAction<EntityType | undefined>('client/updateClientEntityType');
export const updateClientPhone = createAction<string>('client/updateClientPhone');
export const updateClientTaxId = createAction<string>('client/updateClientTaxId');
export const updateClientUcin = createAction<string>('client/updateClientUcin');

export const createNewClient = createAction<IClientDto>('client/createNewClient');
export const addClientToRedux = createAction<IClientDto>('client/addClientToRedux');

export const saveClient = createAsyncThunk<ClientDto, void>(
    'client/saveClient',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const clientState = rootState.clientForm;

        const client = clientState.client;

        myAssert(() => client !== null);

        if (client === null) {
            throw new Error();
        }

        const clientId: number | undefined = client.id;
        let savedClient: ClientDto | undefined;

        if (!clientId || clientId === 0) {
            const addClientPromise = WarehouseClient.addNewClient(client as ClientDto);

            savedClient = await addClientPromise;
        } else {
            const tempClient = new ClientDto(client);

            myAssert(() => tempClient.id !== undefined);

            if (tempClient.id === undefined) {
                throw new Error();
            }

            const clientPromise = WarehouseClient.updateClient(tempClient.id, tempClient as ClientDto);

            savedClient = await clientPromise;
        }

        return savedClient;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const clientState = (thunkAPI.getState() as RootState).clientForm;

            return clientState.client !== null;
        },
    },
);

export const importClients = createAsyncThunk<ClientDto[]>('client/importClients', async (payload, thunkAPI) => {
    return WarehouseClient.getAllClients(true);
});

export const importClient = createAsyncThunk<ClientDto, number>('client/importClient', async (payload, thunkAPI) => {
    return WarehouseClient.getClientById(payload, false);
});

export const importClientForClientDetails = createAsyncThunk<ClientDto, number>(
    'client/importClientForClientDetails',
    async (payload, thunkAPI) => {
        return WarehouseClient.getClientById(payload, true);
    },
);

export const importProjectsForClientDetails = createAsyncThunk<ProjectDto[], number>(
    'client/importProjectsForClientDetails',
    async (payload, thunkAPI) => {
        return WarehouseClient.getAllProjectsForClient(payload, false, true, true);
    },
);

export const clearClientForClientDetails = createAction('client/clearClientForClientDetails');

export const updateClientForClientDetails = createAction<ClientDto>('client/updateClientForClientDetails');

export const updateProjectForClientDetails = createAction<ProjectDto>('client/updateProjectForClientDetails');

export const removeClientForClientDetails = createAction('client/removeClientForClientDetails');

export const removeProjectForClientDetails = createAction<number>('client/removeProjectForClientDetails');

export const addNewProjectForClientDetails = createAction<ProjectDto>('client/addNewProjectForClientDetails');
